// import "firebase/compat/auth";

/**
 * If the user has previously logged in a token will be saved in local storage.
 * This atoms checks if the token exists and if it's valid on init.
 * @module authAtom
 * @category State
 */
import { connectAuthEmulator, getAuth, onAuthStateChanged } from "firebase/auth";
import { type AtomEffect, atom } from "recoil";

import { strings } from "@quickpoint.me/shared";
import rybbit from "@rybbit/js";
import { initializeApp } from "firebase/app";
import type { User } from "firebase/auth";
import type { SetSelfType } from "../@types/state";
import { clearOwnerNameFromSessionStorage } from "../lib/ownerNameSessionStorage";
import type { LocalStorageKeys } from "./uiAtoms";

export type AuthorizationType = {
  // firebase user id
  initializingFireBaseUser: boolean; // when the app starts it will check with any stored OAuth providers to reauthorize
  isLoggedIn: boolean;
  uid: string | undefined;
};

export const authorizationLoggedOut: AuthorizationType = {
  initializingFireBaseUser: false,
  isLoggedIn: false,
  uid: undefined,
};

// Configure Firebase.
export const firebaseConfig = {
  apiKey: "AIzaSyBPyL0yIM4syjMfzgdla6V3N2pbFbDXCTs",
  authDomain: "quickpointauth.firebaseapp.com",
};
// Initialize Firebase
const app = initializeApp(firebaseConfig);
const fbAuth = getAuth(app);

if (process.env.NODE_ENV === "development") {
  // console.log("Auth Emulator Enabled");
  connectAuthEmulator(fbAuth, "http://localhost:9099", { disableWarnings: true });
}



const setUser = async (user: User | null, setSelf: SetSelfType<AuthorizationType>) => {
  if (user) {
    const isLoggedIn = !!user.uid;
    const uid = user.uid;
    // set up an auth object
    const newAuth: AuthorizationType = {
      initializingFireBaseUser: false,
      isLoggedIn,
      uid,
    };

    setSelf(newAuth);

    // Stitch the anonymous session (e.g. the demo tour funnel) to this account
    // in Rybbit. Fires on signup and login; identify is idempotent.
    try {
      rybbit.identify(uid);
    } catch {
      // analytics must never break auth
    }
  } else {
    try {
      rybbit.clearUserId();
    } catch {
      // analytics must never break auth
    }
    clearOwnerNameFromSessionStorage();
    const key: LocalStorageKeys = "auth_token_ts";
    localStorage.setItem(key, "");

    setSelf(authorizationLoggedOut);
  }
}

const authorizationEffect: AtomEffect<AuthorizationType> = ({ setSelf }) => {
  onAuthStateChanged(fbAuth, async (user) => {
    setUser(user, setSelf);
    const key: LocalStorageKeys = "auth_token_ts";
    localStorage.setItem(key, new Date().getTime().toString());
  });
};






export const Authorization = atom<AuthorizationType>({
  key: "Authorization",
  default: {
    initializingFireBaseUser: true,
    isLoggedIn: false,
    uid: undefined,
  },
  effects: [authorizationEffect],

});



/**
 * The single source of truth for which auth screen shows when signed out.
 * "registerName" is not part of this union — that screen is derived state
 * (logged in but no owner name) and routed directly in Auth.tsx.
 */
export type AuthScreenType =
  | "landing"
  | "loginChoices"
  | "loginEmail"
  | "signUp"
  | "signUpEmail";

const authScreenFromPath = (): AuthScreenType => {
  const slug = window.location.pathname.split("/")[1];
  switch (slug) {
    case strings.url.showLogin:
      return "loginChoices";
    case strings.url.userRequestsLoginWithEmail:
      return "loginEmail";
    case strings.url.showSignUp:
    // legacy deep links from old emails/bookmarks land on the signup screen
    case strings.url.userRequestsGoogleSignUp:
    case strings.url.userRequestsGitHubSignUp:
      return "signUp";
    case strings.url.userRequestsSignUpWithEmail:
      return "signUpEmail";
    default:
      return "landing";
  }
};

export const AuthScreenAtom = atom<AuthScreenType>({
  key: "AuthScreen",
  default: "landing",
  effects: [
    ({ setSelf }) => {
      // only override when the URL names a screen, so injected state
      // (tests, SSR) survives a plain "/" path
      const fromPath = authScreenFromPath();
      if (fromPath !== "landing") setSelf(fromPath);
    },
  ],
});

export const OwnerNameExists = atom<boolean>({
  key: "OwnerNameExists",
  default: false,
});

export const CreatingFirebaseAccount = atom<boolean>({
  key: "CreatingFirebaseAccount",
  default: false,
});

export const FirebaseAccountCreated = atom<boolean>({
  key: "FirebaseAccountCreated",
  default: false,
});

export const FirebaseErrorAtom = atom<string | false>({
  key: "FirebaseErrorAtom",
  default: false,
});

export const FirebaseUser = atom<User | undefined>({
  key: "FirebaseUser",
  default: undefined,
});

export const IsCheckingOwnerName = atom<boolean>({
  key: "IsCheckingOwnerName",
  default: false,
});

export const NetworkError = atom<boolean>({
  key: "NetworkError",
  default: false,
});

export const OwnerNameAtom = atom<string>({
  key: "OwnerNameAtom",
  default: strings.unset,
});

export const PersistOwnerNameAtom = atom<string>({
  key: "PersistOwnerNameAtom",
  default: strings.unset,
});

